import React, { useMemo } from 'react';
import { usePaymentDetails } from '../../hooks/usePaymentQueries';
import { useAuth } from '../../hooks/useAuth';
import { isAdmin } from '../../utils/roleUtils';
import PaymentStatusBadge from './PaymentStatusBadge';
import PaymentLink from './PaymentLink';
import { format } from 'date-fns';
import './PaymentDetails.css';

const PaymentDetails = ({ paymentId, onOpenPaymentModal }) => {
  const { data: payment, isLoading, error } = usePaymentDetails(paymentId);
  const { user } = useAuth();
  const isUserAdmin = isAdmin(user);
  const isEmployer = user?.role_name === 'employer';
  const isEmployee = user?.role_name === 'employee';
  const userId = user?.id;

  // Frontend safeguard: Verify user has access to this payment
  // Backend should already filter, but we add a check for security
  const hasAccess = useMemo(() => {
    if (isUserAdmin) return true; // Admins can see all payments
    if (!payment || !userId) return true; // If no payment or user, let it through (backend handles auth)
    
    // Backend should have already filtered, but we do a loose check
    // Use loose comparison to handle string/number ID mismatches
    if (isEmployee) {
      const paymentEmployeeId = payment.employee_id || 
                                payment.employee_detail?.id || 
                                payment.employee?.id;
      // If we can't find employee ID, trust backend (might be nested differently)
      if (paymentEmployeeId == null) return true;
      return String(paymentEmployeeId) === String(userId);
    } else if (isEmployer) {
      const paymentEmployerId = payment.employer_id || 
                                payment.employer_detail?.id || 
                                payment.employer?.id ||
                                payment.job_posting_detail?.employer_id ||
                                payment.job_posting?.employer_id;
      // If we can't find employer ID, trust backend (might be nested differently)
      if (paymentEmployerId == null) return true;
      return String(paymentEmployerId) === String(userId);
    }
    return true; // Default to allowing access - backend should handle filtering
  }, [payment, userId, isUserAdmin, isEmployee, isEmployer]);

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'dd MMM yyyy, HH:mm');
    } catch {
      return dateStr;
    }
  };

  if (isLoading) {
    return (
      <div className="payment-details payment-details--loading">
        <div className="payment-details__spinner"></div>
        <p>Loading payment details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-details payment-details--error">
        <p>Error loading payment details. Please try again.</p>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="payment-details payment-details--empty">
        <p>Payment not found.</p>
      </div>
    );
  }

  // Show access denied if user doesn't have permission to view this payment
  if (!hasAccess) {
    return (
      <div className="payment-details payment-details--error">
        <p>You do not have permission to view this payment.</p>
      </div>
    );
  }

  // Calculate if payout amount differs from total amount
  const totalAmount = parseFloat(payment.total_amount || payment.amount || 0);
  const payoutAmount = parseFloat(payment.employee_payout_amount || 0);
  const platformFee = parseFloat(payment.platform_fee || 0);
  const showPayoutAmount = payoutAmount > 0 && (payoutAmount !== totalAmount || platformFee > 0);

  const employeeValue = payment.employee_detail?.email ||
                        payment.employee?.email || 
                        payment.employee_email ||
                        payment.employee_name ||
                        'N/A';
  const employerValue = payment.employer_detail?.email ||
                        payment.employer_email ||
                        payment.employer_name ||
                        payment.employer?.email ||
                        'N/A';
  const counterpartLabel = isEmployee ? 'Employer' : 'Employee';
  const counterpartValue = isEmployee ? employerValue : employeeValue;

  // Get payment amount and status
  const paymentAmount = isEmployee 
    ? (payment.employee_payout_amount || payment.total_amount || payment.amount)
    : (payment.total_amount || payment.amount);
  const paymentStatus = isEmployer 
    ? payment.employer_payment_status 
    : payment.employee_payout_status;

  return (
    <div className="payment-details">
      <div className="payment-details__two-column">
        {/* Column 1: Employee Email */}
        <div className="payment-details__column payment-details__column--employee">
          <div className="payment-details__column-label">{counterpartLabel}</div>
          <div className="payment-details__column-value payment-details__column-value--email">
            {counterpartValue}
          </div>
        </div>

        {/* Column 2: Payment Amount & Status */}
        <div className="payment-details__column payment-details__column--payment">
          <div className="payment-details__column-label">Payment</div>
          <div className="payment-details__column-content">
            <div className="payment-details__amount">
              {formatCurrency(paymentAmount)}
            </div>
            <div className="payment-details__status">
              <PaymentStatusBadge 
                status={paymentStatus} 
                type={isEmployer ? 'employer' : 'employee'}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Payment button for employers with pending payments */}
      {!isUserAdmin && !isEmployee && isEmployer && (payment.payment_link_url || payment.payment_link) && onOpenPaymentModal && (
        <div className="payment-details__action">
          <button
            className="payment-details__pay-button"
            onClick={onOpenPaymentModal}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="5" width="20" height="14" rx="2"></rect>
              <line x1="2" y1="10" x2="22" y2="10"></line>
            </svg>
            Pay Now
          </button>
        </div>
      )}
    </div>
  );
};

export default PaymentDetails;

